import { fmtUSD, bandLabel } from "./reportingUtils";

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

// --- Narrative summaries (stretch: auto-generated plain-English commentary) ------------------
// Rule-based, deterministic sentences derived from the aggregates — no LLM round-trip needed.
export const buildNarrative = (m) => {
  const out = [];
  if (!m.n) return ["No counterparties to summarize yet."];

  out.push(`The portfolio holds ${m.n.toLocaleString()} counterparties with a total recommended exposure of ${fmtUSD(m.totalRec)} (${fmtUSD(m.totalApp)} approved, ${pct(m.totalApp, m.totalRec)}% of recommended).`);

  if (m.avgScore != null) {
    const highN = m.highRiskRows.length;
    out.push(`The average ARC score is ${m.avgScore.toFixed(1)} — ${bandLabel(m.avgScore).split(" ")[0].toLowerCase()} risk overall — with ${highN} (${pct(highN, m.scoredN)}%) of scored counterparties in the high-risk band.`);
  }

  const topSector = m.bySector[0];
  const riskiest = [...m.bySector].filter((s) => s.avgScore != null).sort((a, b) => b.avgScore - a.avgScore)[0];
  if (topSector) {
    let s = `${topSector.sector} is the largest sector by exposure at ${fmtUSD(topSector.exposure)} (${topSector.count} companies)`;
    if (riskiest && riskiest.sector !== topSector.sector) s += `, while ${riskiest.sector} carries the highest average risk (${riskiest.avgScore})`;
    out.push(s + ".");
  }

  const topRegion = m.byRegion[0];
  if (topRegion) out.push(`Geographically, ${topRegion.region} leads with ${topRegion.count} counterparties and ${fmtUSD(topRegion.exposure)} of exposure.`);

  out.push(`${m.approvedRows.length} counterparties are approved and ${m.pendingRows.length} await review.`);
  return out;
};

// --- Anomaly detection (stretch) -----------------------------------------------------------
// Flags statistically or materially unusual points using simple, explainable heuristics.
export const detectAnomalies = (m, rows) => {
  const flags = [];

  // 1. Sectors whose average risk is well above the portfolio mean.
  if (m.avgScore != null) {
    const scoredSectors = m.bySector.filter((s) => s.avgScore != null && s.count >= 3);
    const vals = scoredSectors.map((s) => s.avgScore);
    const mean = vals.reduce((a, v) => a + v, 0) / (vals.length || 1);
    const sd = Math.sqrt(vals.reduce((a, v) => a + (v - mean) ** 2, 0) / (vals.length || 1)) || 0;
    scoredSectors.forEach((s) => {
      if (sd > 0 && s.avgScore >= mean + 1.5 * sd && s.avgScore > m.avgScore + 1) {
        flags.push({ severity: "high", title: `Elevated risk in ${s.sector}`, detail: `Average ARC ${s.avgScore} vs. portfolio ${m.avgScore.toFixed(1)} — ${s.count} counterparties.` });
      }
    });
  }

  // 2. Concentration risk: a single counterparty that is a large share of total exposure.
  if (m.totalRec > 0 && rows.length) {
    const top = [...rows].sort((a, b) => b.recLimit - a.recLimit)[0];
    const share = pct(top.recLimit, m.totalRec);
    if (share >= 10) flags.push({ severity: share >= 20 ? "high" : "medium", title: "Single-name concentration", detail: `${top.name} is ${share}% of total recommended exposure (${fmtUSD(top.recLimit)}).` });
  }

  // 3. High-risk share above a prudential threshold.
  const highPct = pct(m.highRiskRows.length, m.scoredN);
  if (highPct >= 20) flags.push({ severity: "high", title: "High-risk concentration", detail: `${highPct}% of scored counterparties are high-risk (ARC 6.6+).` });

  // 4. Spike in new counterparties in the latest month vs. the trailing average.
  if (m.overTime.length >= 4) {
    const counts = m.overTime.map((o) => o.count);
    const last = counts[counts.length - 1];
    const prior = counts.slice(0, -1);
    const avg = prior.reduce((a, v) => a + v, 0) / prior.length;
    const sd = Math.sqrt(prior.reduce((a, v) => a + (v - avg) ** 2, 0) / prior.length) || 0;
    if (sd > 0 && last >= avg + 2 * sd && last > avg) {
      flags.push({ severity: "medium", title: "Onboarding spike", detail: `${last} new counterparties in ${m.overTime[m.overTime.length - 1].month} vs. a trailing average of ${avg.toFixed(1)}.` });
    }
  }

  return flags;
};

// --- Basic forecasting (stretch) -----------------------------------------------------------
// Least-squares linear trend projected forward `periods` months on the additions time series.
export const forecastSeries = (overTime, periods = 3) => {
  if (!overTime || overTime.length < 3) return { data: overTime || [], hasForecast: false };
  const y = overTime.map((o) => o.count);
  const n = y.length;
  const xs = y.map((_, i) => i);
  const sx = xs.reduce((a, v) => a + v, 0);
  const sy = y.reduce((a, v) => a + v, 0);
  const sxx = xs.reduce((a, v) => a + v * v, 0);
  const sxy = xs.reduce((a, v, i) => a + v * y[i], 0);
  const denom = n * sxx - sx * sx || 1;
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;

  const [yy, mm] = overTime[overTime.length - 1].month.split("-").map(Number);
  const data = overTime.map((o) => ({ month: o.month, count: o.count, forecast: null }));
  // bridge point so the dashed forecast connects to the last actual
  data[data.length - 1].forecast = data[data.length - 1].count;
  for (let k = 1; k <= periods; k++) {
    const d = new Date(yy, mm - 1 + k, 1);
    const label = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const val = Math.max(0, Math.round(intercept + slope * (n - 1 + k)));
    data.push({ month: label, count: null, forecast: val });
  }
  return { data, hasForecast: true, slope };
};
